import type { RelativeWorkspacePath } from "@codesandbox/pitcher-client";
import { useCopyToClipboard } from "features/common/useCopyToClipboard";
import { usePitcher } from "features/utils/usePitcher";
import {
  Box,
  Button,
  Stack,
  Input,
  Text,
  TextArea,
  Tooltip,
  styled,
  Icon,
} from "prism-react";
import React, { useEffect } from "react";

import { parseEnvironmentString, parseSecretsToString } from "./parse-env";
import type { Secrets } from "./useEnvironmentVariables";

const StretchInput = styled(Input, { flex: 1 });

const UpdatingTooltip: React.FC<{
  isUpdating: boolean;
  children: React.ReactNode;
}> = ({ children, isUpdating }) => {
  if (!isUpdating) {
    return <>{children}</>;
  }

  return <Tooltip content="Operation in progress">{children}</Tooltip>;
};

const NewVariable: React.FC<{
  blockList: string[];
  isUpdating: boolean;
  onAdd: (name: string, value: string) => void;
}> = ({ isUpdating, onAdd, blockList }) => {
  const [name, setName] = React.useState("");
  const [value, setValue] = React.useState("");

  const handleSubmit = () => {
    if (isUpdating) {
      return;
    }

    onAdd(name, value);
    setName("");
    setValue("");
  };

  const error = blockList.includes(name);
  const disabledButton = isUpdating || !name || !value || error;

  return (
    <>
      <form>
        <Stack css={{ paddingLeft: 1, paddingRight: 1 }} horizontal>
          <StretchInput
            css={{ backgroundColor: "$neutral-bg-medium" }}
            onChange={({ target }) => setName(target.value)}
            placeholder="Name"
            value={name}
            autoFocus
          />
          <StretchInput
            css={{ backgroundColor: "$neutral-bg-medium" }}
            onChange={({ target }) => setValue(target.value)}
            placeholder="Value"
            value={value}
          />

          <UpdatingTooltip isUpdating={isUpdating}>
            <Button
              disabled={disabledButton}
              onClick={handleSubmit}
              type="submit"
              variant="primary"
            >
              Add
            </Button>
          </UpdatingTooltip>
        </Stack>
      </form>

      {error && (
        <Text css={{ marginTop: "$2", color: "$informative-error-base" }}>
          Another variable with the same name exists in your project. Remove it
          or choose a diferent name.
        </Text>
      )}
    </>
  );
};

const VariableRow = styled(Stack, {
  "&:not(:last-child)": {
    borderBottom: "1px solid $neutral-bg-high",
  },
  py: "$2",
});

const Variable: React.FC<{
  isUpdating: boolean;
  value: string;
  name: string;
  onDelete: () => void;
}> = ({ isUpdating, value, name, onDelete }) => {
  const [visible, setVisibility] = React.useState(false);
  const copy = useCopyToClipboard();

  return (
    <VariableRow horizontal>
      <Stack css={{ flex: 1 }} gap={0}>
        <Text color="neutral-fg-high">{name}</Text>
        {value ? (
          <Stack horizontal>
            <Tooltip content="Click to copy">
              <Text
                css={{ minWidth: 110, maxWidth: 240, cursor: "default" }}
                onClick={() => copy(value)}
                truncate
              >
                {visible ? value : "•••••••••••••••"}
              </Text>
            </Tooltip>
            <Button
              icon={visible ? "eyeOpen" : "eyeClose"}
              onClick={() => setVisibility((prev) => !prev)}
            />
          </Stack>
        ) : (
          <Stack
            css={{ color: "$informative-warning-base", paddingTop: "$1" }}
            horizontal
          >
            <Icon size={4} type="circleBang" />
            <Text>Missing value</Text>
          </Stack>
        )}
      </Stack>
      <UpdatingTooltip isUpdating={isUpdating}>
        <Button disabled={isUpdating} onClick={() => !isUpdating && onDelete()}>
          Remove
        </Button>
      </UpdatingTooltip>
    </VariableRow>
  );
};

const parseStringToSecrets = (input: string) => parseEnvironmentString(input);

const PlainText: React.FC<{
  secrets: Secrets;
  isUpdating: boolean;
  onSave: (secrets: Secrets) => void;
}> = ({ isUpdating, secrets, onSave }) => {
  const pitcher = usePitcher();

  const [secretsPlainTextInput, setSecretsPlainTextInput] = React.useState(
    parseSecretsToString(secrets),
  );

  useEffect(() => {
    if (Object.keys(secrets).length === 0) {
      pitcher.clients.file
        .openFileByPath("/.env.example" as RelativeWorkspacePath)
        .then((fileRef) => {
          setSecretsPlainTextInput(`# Prefilled from .env.example

${fileRef.object.content.toString()}`);

          fileRef.dispose();
        })
        .catch((_e) => {
          // File might not exist
        });
    }
  }, []);

  return (
    <Box
      as="form"
      css={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "228px",
      }}
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isUpdating) {
          return;
        }

        onSave(parseStringToSecrets(secretsPlainTextInput));
      }}
    >
      <TextArea
        autoCorrect="false"
        css={{
          flex: 1,
          backgroundColor: "$neutral-bg-medium",
        }}
        data-gramm="false"
        disabled={isUpdating}
        onChange={({ target }) =>
          !isUpdating && setSecretsPlainTextInput(target.value)
        }
        spellCheck="false"
        value={secretsPlainTextInput}
      />
      <Box
        css={{
          position: "absolute",
          top: "auto",
          left: "auto",
          right: "$3",
          bottom: "$3",
        }}
      >
        <UpdatingTooltip isUpdating={isUpdating}>
          <Button loading={isUpdating} type="submit" variant="primary">
            Save
          </Button>
        </UpdatingTooltip>
      </Box>
    </Box>
  );
};

export const Form: React.FC<{
  /**
   * isUpdating is true when the state is PUTTING.
   */
  isUpdating: boolean;
  secrets: Secrets;
  /**
   * plainTextMode controls whether to show a form or
   * a text area to edit the variables.
   */
  plainTextMode: boolean;
  onSubmit: (secrets: Secrets) => void;
  onDelete: (key: string, secrets: Secrets) => void;
}> = ({
  isUpdating,
  plainTextMode,
  secrets,
  onSubmit,

  onDelete,
}) => {
  const handleAdd = (name: string, value: string) => {
    /**
     * Intentionally inserting the new value first:
     * - for the sake of order of the list;
     * - `secrets` will never overwrite the new value,
     * thanks to a validation in the form;
     */
    onSubmit({ [name]: value, ...secrets });
  };

  const handleDelete = (name: string) => {
    const newSecrets = { ...secrets };
    delete newSecrets[name];

    onDelete(name, newSecrets);
  };

  const entries = Object.entries(secrets);

  if (plainTextMode) {
    return (
      <PlainText isUpdating={isUpdating} onSave={onSubmit} secrets={secrets} />
    );
  } else {
    return (
      <Stack css={{ flex: 1, minHeight: 0 }}>
        <NewVariable
          blockList={Object.keys(secrets)}
          isUpdating={isUpdating}
          onAdd={handleAdd}
        />
        {entries.length ? (
          <Box css={{ height: "100%", overflow: "auto" }}>
            {entries.map(([name, value]) => (
              <Variable
                key={name}
                isUpdating={isUpdating}
                name={name}
                onDelete={() => handleDelete(name)}
                value={value}
              />
            ))}
          </Box>
        ) : (
          <Stack css={{ height: "100%", textAlign: "center" }}>
            <Text color="neutral-fg-low" css={{ margin: "auto" }}>
              No environment variables added
            </Text>
          </Stack>
        )}
      </Stack>
    );
  }
};
